/**
 * IndexedDB persistence for preferences and news.
 */
import { sources } from "./sources.mjs";
import { topics } from "./topics.mjs";
import { normalizeNews } from "./news-cache.mjs";

const DB_NAME = "semvies";
const DB_STORE = "preferences";
const PREFS_KEY = "user";
const NEWS_STORE = "news";
const topicIds = new Set(topics.map((topic) => topic.id));
const pendingWrites = new Map();

export const DEFAULT_SELECTION = { kind: "topic", id: "headlines" };

function idb() {
  return globalThis.indexedDB ?? null;
}

let dbPromise = null;

function openDb() {
  const impl = idb();
  if (!impl) return Promise.resolve(null);
  if (!dbPromise) {
    dbPromise = new Promise((resolve) => {
      try {
        const request = impl.open(DB_NAME, 2);
        let settled = false;
        const finish = (db) => {
          if (settled) { db?.close(); return; }
          settled = true;
          clearTimeout(timeout);
          resolve(db);
        };
        const timeout = setTimeout(() => finish(null), 1500);
        request.onupgradeneeded = () => {
          const db = request.result;
          if (!db.objectStoreNames.contains(DB_STORE)) db.createObjectStore(DB_STORE);
          if (!db.objectStoreNames.contains(NEWS_STORE)) db.createObjectStore(NEWS_STORE);
        };
        request.onsuccess = () => {
          const db = request.result;
          db.onversionchange = () => { db.close(); dbPromise = null; };
          finish(db);
        };
        request.onblocked = () => finish(null);
        request.onerror = () => finish(null);
      } catch {
        resolve(null);
      }
    });
  }
  return dbPromise;
}

function transact(db, name, mode, operation) {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(name, mode);
    let value;
    const timeout = setTimeout(() => {
      try { tx.abort(); } catch { /* Transaction may already be closed. */ }
      reject(new Error("IndexedDB transaction timed out"));
    }, 1500);
    tx.oncomplete = () => { clearTimeout(timeout); resolve(value); };
    tx.onabort = tx.onerror = () => { clearTimeout(timeout); reject(tx.error); };
    try {
      const request = operation(tx.objectStore(name));
      request.onsuccess = () => { value = request.result; };
    } catch (error) {
      clearTimeout(timeout);
      try { tx.abort(); } catch { /* Already closed. */ }
      reject(error);
    }
  });
}

export async function loadPreferences() {
  const db = await openDb();
  if (!db) return {};
  try {
    const value = await transact(db, DB_STORE, "readonly", (store) => store.get(PREFS_KEY));
    return value && typeof value === "object" ? value : {};
  } catch {
    return {};
  }
}

export async function savePreferences(prefs) {
  const db = await openDb();
  if (!db) return false;
  try {
    await transact(db, DB_STORE, "readwrite", (store) => store.put(prefs ?? {}, PREFS_KEY));
    return true;
  } catch {
    return false;
  }
}

export async function loadNews() {
  const db = await openDb();
  if (!db) return {};
  try {
    const entries = await transact(db, NEWS_STORE, "readonly", (store) => store.getAll());
    return Object.fromEntries(entries
      .filter((entry) => entry && topicIds.has(entry.topic))
      .map((entry) => [entry.topic, normalizeNews(entry.news)]));
  } catch {
    return {};
  }
}

export function saveTopicNews(topic, news) {
  if (!topicIds.has(topic)) return Promise.resolve(false);
  const snapshot = normalizeNews(news);
  const write = (pendingWrites.get(topic) ?? Promise.resolve()).then(async () => {
    const db = await openDb();
    if (!db) return false;
    try {
      await transact(db, NEWS_STORE, "readwrite", (store) => store.put({ topic, news: snapshot }, topic));
      return true;
    } catch {
      return false;
    }
  });
  pendingWrites.set(topic, write);
  void write.finally(() => {
    if (pendingWrites.get(topic) === write) pendingWrites.delete(topic);
  });
  return write;
}

export function selectionScope() {
  return {
    topicIds: topics.map((t) => t.id),
    sourceIds: sources.map((s) => s.id),
  };
}

const sourceAliases = {
  "@gazetadopovorepublica": "@gazetadopovo",
  "@gazetadopovoeconomia": "@gazetadopovo",
  "@uoleconomia": "@uolnoticias",
};

export function readLastSelection(prefs, { topicIds = [], sourceIds = [] } = selectionScope()) {
  const last = prefs?.lastSelection;
  const topicId = last?.id === "daily-life" ? "entertainment" : last?.id;
  if (last?.kind === "topic" && topicIds.includes(topicId)) {
    return { kind: "topic", id: topicId };
  }
  const sourceId = sourceAliases[last?.id] ?? last?.id;
  if (last?.kind === "source" && sourceIds.includes(sourceId)) {
    return { kind: "source", id: sourceId };
  }
  return { ...DEFAULT_SELECTION };
}
